import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { listAdminOrganizations, listAdminSallaInvitations, resendAdminSallaInvitation } from '../../admin/adminApi'
import type { AdminOrganizationSummary, AdminSallaInvitation } from '../../admin/types'
import { Button, Card, Field, I, Pill, gridColumns, mobileButtonStyle } from '../../components/ui'
import { useIsMobileViewport } from '../../hooks/useIsMobileViewport'
import { CodeValue, EmptyState, ErrorState, LoadingState } from './adminPageHelpers'
import { formatDateTime, numberLabel, tableContainerStyle, tdStyle, thStyle } from './adminPageFormatters'

interface AdminOrganizationsPageProps {
  accessToken: string
  onOpenOrganization: (organizationId: number) => void
}

export function AdminOrganizationsPage({ accessToken, onOpenOrganization }: AdminOrganizationsPageProps) {
  const { t, i18n } = useTranslation()
  const isMobile = useIsMobileViewport()
  const [organizations, setOrganizations] = useState<AdminOrganizationSummary[]>([])
  const [invitations, setInvitations] = useState<AdminSallaInvitation[]>([])
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [selectedInvitation, setSelectedInvitation] = useState<AdminSallaInvitation | null>(null)
  const [resending, setResending] = useState(false)
  const [actionError, setActionError] = useState(false)
  const [resentMerchant, setResentMerchant] = useState<string | null>(null)

  const load = useCallback(function load() {
    setLoading(true)
    setError(false)
    Promise.all([listAdminOrganizations(accessToken), listAdminSallaInvitations(accessToken)])
      .then(([nextOrganizations, nextInvitations]) => {
        setOrganizations(nextOrganizations)
        setInvitations(nextInvitations)
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }, [accessToken])

  useEffect(() => {
    void Promise.resolve().then(load)
  }, [load])

  const visibleOrganizations = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return organizations
    return organizations.filter(org => org.name.toLowerCase().includes(needle) || String(org.id).includes(needle))
  }, [organizations, query])

  if (loading) return <LoadingState label={t('admin.organizations.loading')} />
  if (error) return <ErrorState title={t('admin.organizations.error.title')} body={t('admin.organizations.error.body')} onRetry={load} />

  function openRecovery(invitation: AdminSallaInvitation) {
    setActionError(false)
    setSelectedInvitation(invitation)
  }

  function resend() {
    if (!selectedInvitation) return
    setResending(true)
    setActionError(false)
    resendAdminSallaInvitation(accessToken, selectedInvitation.merchantId)
      .then(updated => {
        setInvitations(current => current.map(invitation => invitation.merchantId === updated.merchantId ? updated : invitation))
        setResentMerchant(updated.merchantId)
        setSelectedInvitation(null)
      })
      .catch(() => setActionError(true))
      .finally(() => setResending(false))
  }

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Card>
        <div style={{ display: 'grid', gap: 4 }}>
          <h2 style={{ margin: 0, color: 'var(--brand-900)', fontSize: 20 }}>{t('admin.organizations.invitations.title')}</h2>
          <p style={{ margin: 0, color: 'var(--fg-muted)', fontSize: 14 }}>{t('admin.organizations.invitations.subtitle')}</p>
        </div>
      </Card>

      {resentMerchant && (
        <div role="status" style={{ padding: '12px 16px', borderRadius: 'var(--r-md)', background: 'var(--success-100)', color: 'var(--success-700)', fontWeight: 700 }}>
          {t('admin.organizations.invitations.success', { merchantId: resentMerchant })}
        </div>
      )}

      {invitations.length === 0 ? (
        <EmptyState title={t('admin.organizations.invitations.empty.title')} body={t('admin.organizations.invitations.empty.body')} />
      ) : isMobile ? (
        <div style={{ display: 'grid', gap: 12 }}>
          {invitations.map(invitation => (
            <InvitationCard key={invitation.merchantId} invitation={invitation} onOpen={() => openRecovery(invitation)} />
          ))}
        </div>
      ) : (
        <div style={tableContainerStyle}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={thStyle}>{t('admin.organizations.invitations.table.store')}</th>
              <th style={thStyle}>{t('admin.organizations.invitations.table.recipient')}</th>
              <th style={thStyle}>{t('admin.organizations.invitations.table.organization')}</th>
              <th style={thStyle}>{t('admin.organizations.invitations.table.status')}</th>
              <th style={thStyle}>{t('admin.organizations.invitations.table.expires')}</th>
              <th style={thStyle}>{t('admin.common.actions')}</th>
            </tr></thead>
            <tbody>{invitations.map(invitation => (
              <tr key={invitation.merchantId}>
                <td style={{ ...tdStyle, fontWeight: 800 }}><div>{invitation.storeName ?? t('admin.common.none')}</div><CodeValue>#{invitation.merchantId}</CodeValue></td>
                <td style={tdStyle}>{invitation.recipientEmail ?? t('admin.organizations.invitations.protectedRecipient')}</td>
                <td style={tdStyle}>{invitation.organizationName ?? t('admin.organizations.invitations.unclaimed')}</td>
                <td style={tdStyle}><InvitationStatus status={invitation.status} /></td>
                <td style={tdStyle}>{formatDateTime(invitation.expiresAt, i18n.language, t('admin.common.none'))}</td>
                <td style={tdStyle}><Button kind="ghost" size="sm" onClick={() => openRecovery(invitation)}>{invitation.status === 'PENDING' ? t('admin.organizations.invitations.actions.resend') : t('admin.organizations.invitations.actions.recovery')}</Button></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}

      <Card>
        <div style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, 'minmax(0, 1fr) auto'), gap: 14, alignItems: 'end' }}>
          <Field label={t('admin.organizations.filters.search')} placeholder={t('admin.organizations.filters.searchPlaceholder')} value={query} onChange={event => setQuery(event.target.value)} />
          <Button kind="secondary" icon={I.refresh} onClick={load}>{t('admin.common.refresh')}</Button>
        </div>
      </Card>

      {visibleOrganizations.length === 0 ? (
        <EmptyState title={t('admin.organizations.empty.title')} body={t('admin.organizations.empty.body')} />
      ) : isMobile ? (
        <div style={{ display: 'grid', gap: 12 }}>
          {visibleOrganizations.map(org => (
            <Card key={org.id} padding={18}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'start' }}>
                <div style={{ minWidth: 0 }}>
                  <h2 style={{ margin: 0, color: 'var(--brand-900)', fontSize: 18, overflowWrap: 'anywhere' }}>{org.name}</h2>
                  <div style={{ color: 'var(--fg-subtle)', marginTop: 4 }}><CodeValue>#{org.id}</CodeValue></div>
                </div>
                <Button kind="ghost" size="sm" onClick={() => onOpenOrganization(org.id)}>{t('admin.organizations.actions.open')}</Button>
              </div>
              <div style={{ display: 'grid', gap: 8, marginTop: 14 }}>
                <Meta label={t('admin.organizations.table.users')} value={numberLabel(org.userCount, i18n.language)} />
                <Meta label={t('admin.organizations.table.connections')} value={t('admin.organizations.connectionSummary', { connected: org.connectedChannelConnectionCount + org.connectedInventoryConnectionCount, total: org.channelConnectionCount + org.inventoryConnectionCount })} />
                <Meta label={t('admin.organizations.table.latestActivity')} value={formatDateTime(org.latestActivityAt, i18n.language, t('admin.common.none'))} />
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <div style={tableContainerStyle}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>{t('admin.organizations.table.organization')}</th>
                <th style={thStyle}>{t('admin.organizations.table.users')}</th>
                <th style={thStyle}>{t('admin.organizations.table.connections')}</th>
                <th style={thStyle}>{t('admin.organizations.table.latestActivity')}</th>
                <th style={thStyle}>{t('admin.organizations.table.created')}</th>
                <th style={thStyle}>{t('admin.common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {visibleOrganizations.map(org => (
                <tr key={org.id}>
                  <td style={{ ...tdStyle, color: 'var(--fg)', fontWeight: 800 }}>
                    <div>{org.name}</div>
                    <CodeValue>#{org.id}</CodeValue>
                  </td>
                  <td style={tdStyle}>{numberLabel(org.userCount, i18n.language)}</td>
                  <td style={tdStyle}>{t('admin.organizations.connectionSummary', { connected: org.connectedChannelConnectionCount + org.connectedInventoryConnectionCount, total: org.channelConnectionCount + org.inventoryConnectionCount })}</td>
                  <td style={tdStyle}>{formatDateTime(org.latestActivityAt, i18n.language, t('admin.common.none'))}</td>
                  <td style={tdStyle}>{formatDateTime(org.createdAt, i18n.language, t('admin.common.none'))}</td>
                  <td style={tdStyle}><Button kind="ghost" size="sm" onClick={() => onOpenOrganization(org.id)}>{t('admin.organizations.actions.open')}</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedInvitation && (
        <div role="dialog" aria-modal="true" aria-labelledby="salla-recovery-title" style={{ position: 'fixed', inset: 0, zIndex: 'var(--z-modal)', background: 'rgba(15,23,42,0.55)', display: 'grid', placeItems: 'center', padding: 20 }}>
          <Card style={{ width: 'min(100%, 520px)', boxShadow: 'var(--shadow-lg)' }}>
            <div style={{ display: 'grid', gap: 18 }}>
              <div>
                <h2 id="salla-recovery-title" style={{ margin: 0, color: 'var(--brand-900)', fontSize: 22 }}>
                  {selectedInvitation.status === 'PENDING' ? t('admin.organizations.invitations.dialog.resendTitle') : t('admin.organizations.invitations.dialog.expiredTitle')}
                </h2>
                <p style={{ margin: '6px 0 0', color: 'var(--fg-muted)', lineHeight: 1.6 }}>
                  {selectedInvitation.status === 'PENDING' ? t('admin.organizations.invitations.dialog.resendBody') : t('admin.organizations.invitations.dialog.expiredBody')}
                </p>
              </div>
              <div style={{ display: 'grid', gap: 8, padding: 14, borderRadius: 'var(--r-md)', background: 'var(--ink-50)' }}>
                <Meta label={t('admin.organizations.invitations.table.store')} value={selectedInvitation.storeName ?? `#${selectedInvitation.merchantId}`} />
                <Meta label={t('admin.organizations.invitations.table.recipient')} value={selectedInvitation.recipientEmail ?? t('admin.organizations.invitations.protectedRecipient')} />
                <Meta label={t('admin.organizations.invitations.table.organization')} value={selectedInvitation.organizationName ?? t('admin.organizations.invitations.unclaimed')} />
              </div>
              {selectedInvitation.status === 'EXPIRED' && (
                <ol style={{ margin: 0, paddingInlineStart: 22, color: 'var(--fg)', lineHeight: 1.8 }}>
                  <li>{t('admin.organizations.invitations.dialog.expiredStepOne')}</li>
                  <li>{t('admin.organizations.invitations.dialog.expiredStepTwo')}</li>
                  <li>{t('admin.organizations.invitations.dialog.expiredStepThree')}</li>
                </ol>
              )}
              {actionError && <div role="alert" style={{ color: 'var(--danger-700)', background: 'var(--danger-100)', borderRadius: 'var(--r-md)', padding: 12 }}>{t('admin.organizations.invitations.dialog.error')}</div>}
              <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 10 }}>
                <Button kind="secondary" onClick={() => setSelectedInvitation(null)} style={mobileButtonStyle(isMobile)}>{t('admin.organizations.invitations.dialog.close')}</Button>
                {selectedInvitation.status === 'PENDING' && (
                  <Button onClick={resend} disabled={resending || !selectedInvitation.canResend} style={mobileButtonStyle(isMobile)}>
                    {resending ? t('admin.organizations.invitations.dialog.sending') : selectedInvitation.canResend ? t('admin.organizations.invitations.dialog.confirm') : t('admin.organizations.invitations.dialog.recentlySent')}
                  </Button>
                )}
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  )
}

function InvitationCard({ invitation, onOpen }: { invitation: AdminSallaInvitation; onOpen: () => void }) {
  const { t, i18n } = useTranslation()
  return (
    <Card padding={18}>
      <div style={{ display: 'grid', gap: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 12 }}>
          <div style={{ minWidth: 0 }}><h2 style={{ margin: 0, color: 'var(--brand-900)', fontSize: 18, overflowWrap: 'anywhere' }}>{invitation.storeName ?? t('admin.common.none')}</h2><CodeValue>#{invitation.merchantId}</CodeValue></div>
          <InvitationStatus status={invitation.status} />
        </div>
        <Meta label={t('admin.organizations.invitations.table.recipient')} value={invitation.recipientEmail ?? t('admin.organizations.invitations.protectedRecipient')} />
        <Meta label={t('admin.organizations.invitations.table.organization')} value={invitation.organizationName ?? t('admin.organizations.invitations.unclaimed')} />
        <Meta label={t('admin.organizations.invitations.table.expires')} value={formatDateTime(invitation.expiresAt, i18n.language, t('admin.common.none'))} />
        <Button kind="secondary" onClick={onOpen} style={{ width: '100%', justifyContent: 'center' }}>{invitation.status === 'PENDING' ? t('admin.organizations.invitations.actions.resend') : t('admin.organizations.invitations.actions.recovery')}</Button>
      </div>
    </Card>
  )
}

function InvitationStatus({ status }: { status: AdminSallaInvitation['status'] }) {
  const { t } = useTranslation()
  return <Pill tone={status === 'PENDING' ? 'warning' : 'danger'} dot>{t(`admin.organizations.invitations.status.${status}`)}</Pill>
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13 }}>
      <span style={{ color: 'var(--fg-subtle)', fontWeight: 700 }}>{label}</span>
      <span style={{ color: 'var(--fg)', textAlign: 'end' }}>{value}</span>
    </div>
  )
}
